import {lazy, Suspense, useEffect, useState} from 'react';
import styled from 'styled-components';
import {Box} from '@sproutsocial/seeds-react-box';
import FeatureBanner from './feature-banner';
import Logo from './logo';
import SocialLinks from './social-links';
import Description from './description';
import ButtonsList from './buttons-list';
import {captureError} from '../captureError';
import {pollinateSproutLink} from '../pollination';
import {getPageData, getPostsData, type TypePageData, type TypePostData} from '../services';
import {getHostname, getProfileFromUrl, setMetadataForPage, REDIRECT_URL, CONTAINER_MAX_WIDTH} from '../utils';

const PostsGrid = lazy(() => import(/* webpackChunkName: "posts-grid", webpackPrefetch: true */ './posts-grid'));
const ErrorBanner = lazy(() =>
	import(/* webpackChunkName: "error-banner" */ './error-banner').then(m => ({default: m.ErrorBanner}))
);

const PageContainer = styled(Box)`
	background-color: ${props => props.theme.colors.container.background.base};
	margin: 0 auto;
	max-width: ${CONTAINER_MAX_WIDTH};
	display: flex;
	flex-direction: column;
	gap: ${props => props.theme.space[350]};
	padding: ${props => props.theme.space[450]} 0;
`;

export default function App() {
	const profile = getProfileFromUrl();
	const [hasError, setHasError] = useState(false);
	const [posts, setPosts] = useState<TypePostData[]>([]);
	const [page, setPage] = useState<TypePageData | null | undefined>(null);

	useEffect(() => {
		if (!page) {
			return;
		}
		const {page_id} = page;
		const referrer = getHostname(document.referrer);

		pollinateSproutLink(
			{
				feature: 'page',
				action: String(page_id || 0),
				trigger: 'load',
				id: page_id,
			},
			referrer ? {referrer} : undefined
		);
	}, [page]);

	useEffect(() => {
		if (!profile) {
			window.location.assign(REDIRECT_URL);
			return;
		}

		getPageData(profile)
			.then(page => {
				setPage(page);
				setMetadataForPage(page);
			})
			.catch(error => {
				captureError(error, {tags: {request: 'page-fetch', profile}});
				window.location.assign(REDIRECT_URL);
			});

		getPostsData(profile)
			.then(posts => setPosts(posts))
			.catch(error => {
				// Only show error banner if posts fail
				setHasError(true);
				captureError(error, {tags: {request: 'posts-fetch', profile}});
			});
	}, [profile]);

	return (
		<PageContainer as='main'>
			{hasError && (
				<Suspense fallback={null}>
					<ErrorBanner />
				</Suspense>
			)}
			<Logo page={page} />
			<FeatureBanner page={page} />
			<SocialLinks page={page} />
			<Description page={page} />
			<ButtonsList page={page} />
			<Suspense fallback={null}>
				<PostsGrid page={page} posts={posts} />
			</Suspense>
		</PageContainer>
	);
}
